// Contact administrator — the details the mobile app shows when a user needs
// to reach someone for help.
//
// Both numbers stay SPLIT end to end: PhoneInput holds a dial code and national
// digits separately, and the contract takes them as two fields each. The dial
// code carries its leading '+', matching mocks/countries.ts and every other
// phone-sending endpoint in the app.
//
// The three contact fields each sit behind a leading icon tile, per the design.
// The tile is a page-scoped sibling of the control, NOT a component change —
// PhoneInput stays exactly as every other form in the app uses it.
//
// Validation is required-only, deliberately: the auth screens apply a 7–12
// digit rule because they gate a login, but this is display data and a company
// with an unusual number should not be blocked from saving its own card.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import * as yup from 'yup';
import SettingsCard, { SettingsRow } from './SettingsCard';
import { SUPPORT_DAYS, TIME_SLOTS, labelOf, toSelectOptions } from './companySettingsOptions';
import { Field, Icon, Input, PhoneInput, SelectDropdown } from '@/components/common';
import {
  nullIfEmpty,
  useUpdateSupportContactMutation,
  type SupportContact,
} from '@/api/endpoints/companySettingsApi';
import { countries } from '@/mocks/countries';
import type { ApiErrorShape } from '@/api/types';

interface ContactAdministratorCardProps {
  support: SupportContact;
}

const emailSchema = yup.string().email();

const ContactAdministratorCard = ({ support }: ContactAdministratorCardProps) => {
  const { t } = useTranslation();
  const [updateSupport, { isLoading: saving }] = useUpdateSupportContactMutation();

  const seed = useMemo<SupportContact>(() => ({ ...support }), [support]);
  const [draft, setDraft] = useState<SupportContact>(seed);
  const [editing, setEditing] = useState(false);
  useEffect(() => setDraft(seed), [seed]);

  const patch = (changes: Partial<SupportContact>) => setDraft((d) => ({ ...d, ...changes }));

  const emailInvalid = !!draft.email.trim() && !emailSchema.isValidSync(draft.email.trim());
  const dirty = (Object.keys(seed) as (keyof SupportContact)[]).some((k) => draft[k] !== seed[k]);
  const canSave = dirty && !emailInvalid;

  // The design's single line, composed from the three stored values. Rendered
  // only once all three are set — a half-filled window reads as broken.
  const hoursComplete = !!(seed.supportDays && seed.supportStartTime && seed.supportEndTime);
  // Every part goes through labelOf: the stored day is hyphenated and the
  // stored times are 24-hour HH:mm, so none of the raw values is what the user
  // should read.
  const hoursLine = hoursComplete
    ? t('company_settings_hours_composed', {
        days: labelOf(SUPPORT_DAYS, seed.supportDays, t),
        start: labelOf(TIME_SLOTS, seed.supportStartTime, t),
        end: labelOf(TIME_SLOTS, seed.supportEndTime, t),
      })
    : '';

  const draftHoursComplete = !!(
    draft.supportDays &&
    draft.supportStartTime &&
    draft.supportEndTime
  );

  const cancel = () => {
    setDraft(seed);
    setEditing(false);
  };

  const save = async () => {
    try {
      // A dial code with no number behind it is meaningless, so each pair goes
      // null together rather than leaving a stray '+966' attached to nothing.
      const mobile = nullIfEmpty(draft.mobileNumber);
      const whatsApp = nullIfEmpty(draft.whatsAppNumber);

      await updateSupport({
        name: nullIfEmpty(draft.name),
        email: nullIfEmpty(draft.email),
        mobileNumberCountryCode: mobile && nullIfEmpty(draft.mobileNumberCountryCode),
        mobileNumber: mobile,
        whatsAppNumberCountryCode: whatsApp && nullIfEmpty(draft.whatsAppNumberCountryCode),
        whatsAppNumber: whatsApp,
        supportDays: nullIfEmpty(draft.supportDays),
        supportStartTime: nullIfEmpty(draft.supportStartTime),
        supportEndTime: nullIfEmpty(draft.supportEndTime),
      }).unwrap();
      setEditing(false);
      toast.success(t('company_settings_support_saved'));
    } catch (err) {
      toast.error((err as ApiErrorShape)?.msg ?? t('error_generic'));
    }
  };

  return (
    <SettingsCard
      title={t('company_settings_support_title')}
      subtitle={t('company_settings_support_subtitle')}
      editing={editing}
      dirty={dirty}
      saving={saving}
      canSave={canSave}
      onEdit={() => setEditing(true)}
      onCancel={cancel}
      onSave={() => void save()}
    >
      {editing ? (
        <div className='company-settings__form'>
          <Field
            label={t('company_settings_admin_name')}
            labelTag={t('company_settings_admin_name_hint')}
          >
            <Input value={draft.name} onChange={(e) => patch({ name: e.target.value })} />
          </Field>

          <Field label={t('company_settings_phone')}>
            <div className='company-settings__with-icon'>
              <span className='company-settings__field-icon' aria-hidden='true'>
                <Icon name='phone' size={18} />
              </span>
              <PhoneInput
                countries={countries}
                hideBadge
                dialCode={draft.mobileNumberCountryCode}
                onDialCodeChange={(dial) => patch({ mobileNumberCountryCode: dial })}
                value={draft.mobileNumber}
                onChange={(digits) => patch({ mobileNumber: digits })}
              />
            </div>
          </Field>

          <Field label={t('company_settings_whatsapp')}>
            <div className='company-settings__with-icon'>
              <span className='company-settings__field-icon' aria-hidden='true'>
                <Icon name='message-circle' size={18} />
              </span>
              <PhoneInput
                countries={countries}
                hideBadge
                dialCode={draft.whatsAppNumberCountryCode}
                onDialCodeChange={(dial) => patch({ whatsAppNumberCountryCode: dial })}
                value={draft.whatsAppNumber}
                onChange={(digits) => patch({ whatsAppNumber: digits })}
              />
            </div>
          </Field>

          <Field
            label={t('company_settings_support_email')}
            error={emailInvalid ? t('error_invalid_email') : undefined}
          >
            <div className='company-settings__with-icon'>
              <span className='company-settings__field-icon' aria-hidden='true'>
                <Icon name='mail' size={18} />
              </span>
              <Input
                type='email'
                dir='ltr'
                value={draft.email}
                error={emailInvalid}
                onChange={(e) => patch({ email: e.target.value })}
              />
            </div>
          </Field>

          <div className='company-settings__row'>
            <p className='company-settings__label'>
              {t('company_settings_support_hours')}
              <span className='company-settings__label-spec'>
                {t('company_settings_support_hours_hint')}
              </span>
            </p>
            <div className='company-settings__hours'>
              <SelectDropdown
                size='lg'
                className='company-settings__hours-days'
                options={toSelectOptions(SUPPORT_DAYS, t)}
                value={draft.supportDays || null}
                onChange={(supportDays) => patch({ supportDays })}
                aria-label={t('company_settings_support_hours')}
              />
              <SelectDropdown
                size='lg'
                options={toSelectOptions(TIME_SLOTS, t)}
                value={draft.supportStartTime || null}
                onChange={(supportStartTime) => patch({ supportStartTime })}
                aria-label={t('company_settings_support_start')}
              />
              <SelectDropdown
                size='lg'
                options={toSelectOptions(TIME_SLOTS, t)}
                value={draft.supportEndTime || null}
                onChange={(supportEndTime) => patch({ supportEndTime })}
                aria-label={t('company_settings_support_end')}
              />
            </div>
            {draftHoursComplete && (
              <p className='company-settings__hint'>
                {t('company_settings_hours_preview', {
                  line: t('company_settings_hours_composed', {
                    days: labelOf(SUPPORT_DAYS, draft.supportDays, t),
                    start: labelOf(TIME_SLOTS, draft.supportStartTime, t),
                    end: labelOf(TIME_SLOTS, draft.supportEndTime, t),
                  }),
                })}
              </p>
            )}
          </div>
        </div>
      ) : (
        <div className='company-settings__rows'>
          <SettingsRow label={t('company_settings_admin_name')} value={seed.name} />
          <SettingsRow
            label={t('company_settings_phone')}
            value={
              seed.mobileNumber ? `${seed.mobileNumberCountryCode} ${seed.mobileNumber}`.trim() : ''
            }
            ltr
          />
          <SettingsRow
            label={t('company_settings_whatsapp')}
            value={
              seed.whatsAppNumber
                ? `${seed.whatsAppNumberCountryCode} ${seed.whatsAppNumber}`.trim()
                : ''
            }
            ltr
          />
          <SettingsRow label={t('company_settings_support_email')} value={seed.email} ltr />
          <SettingsRow label={t('company_settings_support_hours')} value={hoursLine} />
        </div>
      )}
    </SettingsCard>
  );
};

export default ContactAdministratorCard;
